import { apiClient } from './client';
import type {
  SearchConfiguration,
  ClientSettings,
  FreeTextSearchResponse,
} from './types';
import dayjs from 'dayjs';

const isNumeric = (value: string): boolean =>
  /^-?\d+(\.\d+)?$/.test(value.trim());

export const getAirportCodes = (airportCodes: string[]): string[] => {
  return airportCodes.filter((x: string) => !isNumeric(x));
};

export const getAirportGroups = (airportCodes: string[]): string[] => {
  return airportCodes.filter((x: string) => isNumeric(x));
};

/**
 * Every departure a Search Configuration selects — airport codes and airport
 * group ids mixed, split later by {@link getAirportCodes} / {@link getAirportGroups}.
 *
 * `selections` holds the full multi-select; `selectedId` is only the first of
 * them, kept for older configurations saved before multi-select existed, so
 * it is the fallback, never the source.
 */
export const getDepartureIds = (search: SearchConfiguration): string[] => {
  const selections = search.departure?.selections ?? [];
  if (selections.length > 0) return selections.map(x => x.id);
  return search.departure?.selectedId ? [search.departure.selectedId] : [];
};

export const getFlightDates = (
  search: SearchConfiguration
): { departureDate: string; maxDepartureDate: string } => {
  let departureDate = search.travelSchedule?.outbound?.dateRange?.min ?? '';
  let maxDepartureDate = search.travelSchedule?.outbound?.dateRange?.max ?? '';

  if (search.travelSchedule?.rollingDates?.enabled) {
    departureDate = dayjs()
      .add(search.travelSchedule.rollingDates.minDepartureDays, 'day')
      .format('YYYY-MM-DD');
    maxDepartureDate = dayjs()
      .add(search.travelSchedule.rollingDates.maxDepartureDays, 'day')
      .format('YYYY-MM-DD');
  }

  return { departureDate, maxDepartureDate };
};

export const getResults = async (
  search: SearchConfiguration,
  clientSettings: ClientSettings,
  numberOfResults: number
) => {
  const airportCodes = getDepartureIds(search);

  const flightDates = getFlightDates(search);

  const minDuration = Number.parseInt(search.duration.slice(1, 3));
  let maxDuration = Number.parseInt(search.duration.slice(3, 5));
  if (minDuration > maxDuration) maxDuration = minDuration;

  const query = {
    minDepartureDate: flightDates.departureDate,
    maxDepartureDate: flightDates.maxDepartureDate,
    minDuration: minDuration,
    maxDuration: maxDuration,
    adults: String(search.passengers.adults),
    childAges: search.passengers.childAges.join(',') || undefined,
    destinations:
      (search.destinations?.selectedIds ?? []).join(',') || undefined,
    accommodations:
      (search.accommodations?.selectedIds ?? []).join(',') || undefined,
    options: search.options?.join(',') || undefined,
    airports: getAirportCodes(airportCodes).join(',') || undefined,
    airportGroups: getAirportGroups(airportCodes).join(',') || undefined,
    minOutboundFlightTime:
      search.travelSchedule?.outbound?.flightTime?.min ?? '00:00',
    maxOutboundFlightTime:
      search.travelSchedule?.outbound?.flightTime?.max ?? '23:59',
    minInboundFlightTime:
      search.travelSchedule?.inbound?.flightTime?.min ?? '00:00',
    maxInboundFlightTime:
      search.travelSchedule?.inbound?.flightTime?.max ?? '23:59',
    minBudget: search.budget?.min ?? 0,
    maxBudget: search.budget?.max ?? 6000,
    market: clientSettings.market,
    country: clientSettings.country,
    language: clientSettings.language,
    currency: clientSettings.currency,
    page: 1,
    pageSize: numberOfResults,
    sort:
      search.sortOrder === undefined || (search.sortOrder as string) === 'price'
        ? 'PriceAsc'
        : search.sortOrder,
  };

  try {
    const { data, error } = await apiClient.GET('/packages/products', {
      params: { query },
    });

    if (error) throw error;

    return data;
  } catch (err) {
    console.log(JSON.stringify(query));
    console.warn(err);
    throw err;
  }
};

export const getFilters = async (clientSettings: ClientSettings) => {
  try {
    const { data, error } = await apiClient.GET('/packages/filters', {
      params: { query: clientSettings },
    });

    if (error) throw error;

    return data;
  } catch (err) {
    console.log(JSON.stringify(clientSettings));
    console.warn(err);
    throw err;
  }
};

export const getFreeTextSearchResults = async (
  search: string,
  clientSettings: ClientSettings
): Promise<FreeTextSearchResponse | undefined> => {
  try {
    const { data, error } = await apiClient.GET('/destinations', {
      params: {
        query: {
          ...clientSettings,
          q: search.trim(),
        },
      },
    });

    if (error) throw error;

    return data;
  } catch (err) {
    console.log(JSON.stringify(clientSettings));
    console.warn(err);
    throw err;
  }
};
